/**
 * Error Boundary – catches render errors in children and shows a branded fallback.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { AppErrorPage } from '@pages/AppErrorPage';
import { Sentry } from '@utils/sentry';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback UI when an error is caught. */
  fallback?: ReactNode;
  /** Called when an error is caught (also logged to console.error). */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Render error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <AppErrorPage
          message={this.state.error.message || 'Something unexpected happened while loading this page.'}
          errorCode="APP_RENDER_ERROR"
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}
